import { useEffect, useState, type FormEvent } from "react";
import { getMultiFactorResolver, GoogleAuthProvider, signInWithEmailAndPassword, signInWithPopup, type MultiFactorError, type MultiFactorResolver } from "firebase/auth";
import { useLocation } from "wouter";
import { Button, Card, Input, Label } from "@clevis/shared-ui";
import { auth } from "../../lib/firebase";
import { call } from "../../lib/callFunction";
import { MessageBanner } from "../../components/MessageBanner";
import { TwoStepChallenge } from "../../components/TwoStepChallenge";
import { authCode, markFreshSignIn } from "../../lib/twoStep";

export function SignIn() {
  const [, navigate] = useLocation();
  const [mode, setMode] = useState<"signin" | "invite">("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [inviteToken, setInviteToken] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  // Set when the account has two-step sign-in: the code step comes next.
  const [resolver, setResolver] = useState<MultiFactorResolver | null>(null);
  const signedIn = () => {
    markFreshSignIn();
    navigate("/");
  };
  const failed = (err: unknown) => {
    if (authCode(err) === "auth/multi-factor-auth-required") setResolver(getMultiFactorResolver(auth, err as MultiFactorError));
    else setError((err as Error).message.replace("Firebase: ", ""));
  };

  // Invite emails link to /signin?invite=TOKEN — land straight in the
  // create-account step with the token filled in.
  useEffect(() => {
    const token = new URLSearchParams(window.location.search).get("invite");
    if (token && token.length >= 16) {
      setInviteToken(token);
      setMode("invite");
    }
  }, []);

  async function signInEmail(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await signInWithEmailAndPassword(auth, email, password);
      signedIn();
    } catch (err) {
      failed(err);
    } finally {
      setBusy(false);
    }
  }

  async function signInGoogle() {
    setBusy(true);
    setError("");
    try {
      await signInWithPopup(auth, new GoogleAuthProvider());
      signedIn();
    } catch (err) {
      failed(err);
    } finally {
      setBusy(false);
    }
  }

  // Account creation happens server-side: the invite token is the permission
  // to exist. No invite, no account — that's the bot gate.
  async function registerInvite(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const res = await call<{ email: string }>(
        "registerWithInvite",
        { token: inviteToken.trim(), password, displayName },
        { mutation: false },
      );
      await signInWithEmailAndPassword(auth, res.email, password);
      signedIn();
    } catch (err) {
      setError((err as Error).message.replace("Firebase: ", ""));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-full flex-col">
      <MessageBanner context="login" />
      <div className="flex flex-1 items-center justify-center p-4">
      <Card className="w-full max-w-sm">
        <div className="mb-6 text-center">
          <img src="/favicon-96x96.png" alt="" className="mx-auto mb-2 h-12 w-12" />
          <h1 className="text-xl font-bold text-ink">Clevis</h1>
          <p className="text-xs text-steel">Field service, without the fight.</p>
        </div>

        {resolver ? (
          <TwoStepChallenge
            resolver={resolver}
            onDone={signedIn}
            onCancel={() => {
              setResolver(null);
              setPassword("");
            }}
          />
        ) : mode === "signin" ? (
          <>
            <form onSubmit={signInEmail} className="space-y-3">
              <div>
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
              </div>
              <div>
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
              {error ? <p className="text-xs text-danger">{error}</p> : null}
              <Button type="submit" busy={busy} className="w-full">
                Sign in
              </Button>
            </form>
            <div className="my-3 flex items-center gap-2 text-[10px] uppercase tracking-wide text-steel">
              <span className="h-px flex-1 bg-steel/25" /> or <span className="h-px flex-1 bg-steel/25" />
            </div>
            <Button variant="ghost" className="w-full border border-steel/30" onClick={signInGoogle} busy={busy}>
              <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true">
                <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.5l6.7-6.7C35.7 2.5 30.2 0 24 0 14.6 0 6.5 5.4 2.5 13.2l7.8 6.1C12.2 13.3 17.6 9.5 24 9.5z" />
                <path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.7c-.6 3-2.3 5.5-4.8 7.2l7.5 5.8c4.4-4.1 7.1-10.1 7.1-17.5z" />
                <path fill="#FBBC05" d="M10.3 28.7a14.6 14.6 0 0 1 0-9.4l-7.8-6.1a24 24 0 0 0 0 21.6l7.8-6.1z" />
                <path fill="#34A853" d="M24 48c6.2 0 11.4-2 15.2-5.5l-7.5-5.8c-2.1 1.4-4.7 2.3-7.7 2.3-6.4 0-11.8-3.8-13.7-9.3l-7.8 6.1C6.5 42.6 14.6 48 24 48z" />
              </svg>
              Continue with Google
            </Button>
            <button
              className="mt-3 w-full text-center text-xs text-steel hover:text-ink"
              onClick={() => {
                setMode("invite");
                setError("");
              }}
            >
              Have an invite? Create your account
            </button>
          </>
        ) : (
          <>
            <form onSubmit={registerInvite} className="space-y-3">
              <div>
                <Label htmlFor="token">Invite token</Label>
                <Input
                  id="token"
                  required
                  minLength={16}
                  value={inviteToken}
                  onChange={(e) => setInviteToken(e.target.value)}
                  placeholder="Paste the token from your invite"
                />
              </div>
              <div>
                <Label htmlFor="name">Your name</Label>
                <Input id="name" value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
              </div>
              <div>
                <Label htmlFor="newpass">Choose a password</Label>
                <Input
                  id="newpass"
                  type="password"
                  required
                  minLength={8}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
              {error ? <p className="text-xs text-danger">{error}</p> : null}
              <Button type="submit" busy={busy} className="w-full">
                Create account
              </Button>
            </form>
            <p className="mt-2 text-center text-[11px] text-steel">
              Accounts are invite-only. Google users: just press "Continue with Google" — your invite is
              matched by email.
            </p>
            <button
              className="mt-3 w-full text-center text-xs text-steel hover:text-ink"
              onClick={() => {
                setMode("signin");
                setError("");
              }}
            >
              Back to sign in
            </button>
          </>
        )}
      </Card>
      </div>
    </div>
  );
}
